import { ChannelType, SupportMessageType } from '../types/atendimentoEnums';
import { Conversation, ConversationLastMessagePreview } from '../types/conversation';
import { SupportMessage } from '../types/supportMessage';

const EMPTY_PREVIEW = 'Sem conteúdo';

const readNestedString = (json: string | null | undefined, ...path: string[]): string | null => {
  if (!json)
  {
    return null;
  }

  try
  {
    let current: unknown = JSON.parse(json);
    for (const key of path)
    {
      if (!current || typeof current !== 'object')
      {
        return null;
      }

      current = (current as Record<string, unknown>)[key];
    }

    return typeof current === 'string' && current.trim().length > 0 ? current : null;
  }
  catch
  {
    return null;
  }
};

export const formatQueuePreview = (preview?: string | null): string => {
  const text = (preview ?? '').replace(/\s+/g, ' ').trim();
  return text.length > 0 ? text : EMPTY_PREVIEW;
};

export const formatUnreadBadge = (count: number): string => {
  if (count > 9)
  {
    return '9+';
  }

  return String(count);
};

export const formatUnreadDividerLabel = (count: number): string => {
  if (count <= 0)
  {
    return '';
  }

  const display = formatUnreadBadge(count);
  if (count === 1)
  {
    return `${display} mensagem não lida`;
  }

  return `${display} mensagens não lidas`;
};

const toSingleLine = (value: string): string => value.replace(/\s+/g, ' ').trim();

export const resolveWorkspaceEmailSubject = (
  conversation: Pick<Conversation, 'channelType' | 'customAttributes'> | null | undefined,
  messages: Array<Pick<SupportMessage, 'contentAttributes'>> = [],
): string | null => {
  if (!conversation || conversation.channelType !== ChannelType.Email)
  {
    return null;
  }

  const fromConversation = readNestedString(conversation.customAttributes, 'emailSubject');
  if (fromConversation)
  {
    return toSingleLine(fromConversation);
  }

  for (const message of messages)
  {
    const fromMessage = readNestedString(message.contentAttributes, 'email', 'subject');
    if (fromMessage)
    {
      return toSingleLine(fromMessage);
    }
  }

  return null;
};

export const previewFromSupportMessage = (
  message: Pick<SupportMessage, 'content' | 'contentAttributes'>,
  conversationCustomAttributes?: string | null,
): string => {
  const subject = readNestedString(message.contentAttributes, 'email', 'subject')
    ?? readNestedString(conversationCustomAttributes, 'emailSubject');

  return formatQueuePreview(subject || message.content);
};

export const toLastNonActivityPreview = (
  message: SupportMessage,
  conversationCustomAttributes?: string | null,
): ConversationLastMessagePreview | null => {
  if (message.messageType === SupportMessageType.Activity)
  {
    return null;
  }

  return {
    id: message.id,
    messageType: message.messageType,
    isPrivate: message.isPrivate,
    preview: previewFromSupportMessage(message, conversationCustomAttributes),
    createdAt: message.createdAt,
  };
};

export const isUnreadIncomingMessage = (message: Pick<SupportMessage, 'messageType' | 'isPrivate'>): boolean => {
  return message.messageType === SupportMessageType.Incoming && !message.isPrivate;
};

export const resolveUnreadDivider = (
  messages: Array<Pick<SupportMessage, 'messageType' | 'isPrivate' | 'createdAt'>>,
  lastSeenAt: string | null | undefined,
): { firstIndex: number; count: number } => {
  const lastSeenMs = lastSeenAt ? new Date(lastSeenAt).getTime() : null;
  let firstIndex = -1;
  let count = 0;

  messages.forEach((message, index) => {
    if (!isUnreadIncomingMessage(message))
    {
      return;
    }

    const createdMs = new Date(message.createdAt).getTime();
    const isUnread = lastSeenMs == null || createdMs > lastSeenMs;
    if (!isUnread)
    {
      return;
    }

    if (firstIndex < 0)
    {
      firstIndex = index;
    }

    count += 1;
  });

  return { firstIndex, count };
};
